import React, { Suspense, useEffect, useState } from 'react';
import { Grid2 as Grid, ThemeProvider } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { SALT_PAN, theme } from '../../Assets/Constants/theme.js';
import {
	setAutoSave,
	updateCkbuttonStatus
} from '../../Store/Redux/ckeditor-slice.js';
import { checkNotificationStatus } from '../../Store/DashboardSlice/dashboard-slice.js';
import { fetchMenuDetail, refreshtoken } from '../../Services/api-config.js';
import {
	setAccessToken,
	updateRedirectRoute,
	updateUserInfo
} from '../../Store/AuthSlice/auth-slice.js';
import Loading from '../Common/LoaderComponent/loader.js';
import {
	decodeJwtPayload,
	getErrorMessage,
	handleLogoutUtil,
	routerData
} from '../../utils/util.js';
import customToast from '../Common/custom-toast.js';
import SuccessRejectPopUp from '../Common/success-reject-pop-up.js';
import { AUTHKEY, REFRESHKEY } from '../../utils/keys.js';
import { sideBarMenu } from '../../Assets/Constants/json_constants.js';
import { routeConstants } from '../../utils/path-constants.js';
import useAccountType from '../Hooks/use-account-type.js';
import Header from './header.js';
import SideBar from './sidebar.js';

const allowedPages = new Set(['uam', 'dashboard', 'wallet']);

export default function MainLayout() {
	const [isOpen, setIsOpen] = useState(false);
	const { showToast } = customToast();
	const dispatch = useDispatch();
	const location = useLocation();
	const navigate = useNavigate();
	const [menuItems, setMenuItems] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const userData = useSelector((state) => state.auth.userInfo);
	const isMobile = useMediaQuery('(max-width:768px)');
	const [userLoggedIn, setUserLoggedIn] = useState(false);
	const [refreshToken, setRefreshToken] = useState(true);
	const accountType = useAccountType();

	const getPermissions = (menu, category, subcategory, permissionName) => {
		const subcategoryPermissions = menu?.[category]?.[subcategory] || [];
		if (permissionName) {
			return subcategoryPermissions.some(
				(item) => item.permissionName === permissionName
			);
		}

		return subcategoryPermissions.length > 0;
	};

	const refreshAccessToken = async () => {
		try {
			const refreshToken = window.localStorage.getItem(REFRESHKEY);
			if (!refreshToken) {
				navigate('/login', { replace: true });
				setUserLoggedIn(false);
				return;
			}

			const response = await refreshtoken({ refreshToken });
			if (response?.status === 201) {
				const newAccessToken = response?.token;
				window.localStorage.setItem(AUTHKEY, newAccessToken);
				dispatch(setAccessToken(newAccessToken));
				setRefreshToken(false);
			} else {
				if (response.status === 401 || response.status === 400) {
					showToast('error', 'Session expired. Please login again.');
					handleLogoutUtil(dispatch, navigate);
					return;
				}

				showToast('error', getErrorMessage(response));
			}
		} catch (error) {
			showToast('error', getErrorMessage(error));
		}
	};

	const fetchMenuDetails = async () => {
		setIsLoading(true);

		const response = await fetchMenuDetail();

		if (response?.status === 200) {
			const userDetails = response.data?.[0];
			const { permissions: menu, userType } = userDetails;

			setIsLoading(false);
			setMenuItems(response.data);

			dispatch(
				updateUserInfo({
					menu,
					emailId: userDetails.emailId,
					userId: userDetails.userId,
					brandingChange: false,
					profileImageChanged: true,
					branding: userDetails.branding,
					orgId: userDetails.orgId,
					userName: userDetails.userName,
					userType,
					isAdmin: userDetails?.isSuperAdmin,
					approvalEsignFlow: userDetails?.approvalEsignFlow,
					servicesType: userDetails?.servicesType,
					activeBranchAliasData: userDetails?.activeBranchAliasData,
					organizationRegType: userDetails?.organizationRegType
				})
			);

			window.scrollTo(0, 0);

			const checkRoutePermission = (route, permissionCheck) =>
				location.pathname === route && permissionCheck;

			const link = localStorage.getItem('approval_link');

			if (link) {
				const segments = link.trimStart().split('/');
				const redirectPage = segments[2];

				const isValidPage = allowedPages.has(redirectPage);

				const hasPermission = (() => {
					switch (redirectPage) {
					case 'dashboard': {
						return menu?.Approval?.dashboard_approval_request;
					}

					case 'uam': {
						return menu?.Approval?.uam_approval_request;
					}

					case 'wallet': {
						return menu?.Approval?.wallet_approval_request;
					}

					default: {
						return false;
					}
					}
				})();

				if (segments.length > 2 && isValidPage && hasPermission) {
					navigate(`/${redirectPage}`);
					return;
				}
			} else {
				localStorage.removeItem('approval_link');
			}

			if (accountType === 'individual') {
				menuNavigate(menu, userType, false);
				return;
			}

			if (location.pathname === routeConstants.PROFILE) {
				menuNavigate(menu, userType, false);
				return;
			}

			if (
				checkRoutePermission(
					routeConstants.UAM,
					menu?.UAM || menu?.Approval?.uam_approval_request
				)
				|| checkRoutePermission(
					routeConstants.DASHBOARD,
					hasDashboardPermission(menu)
				)
				|| checkRoutePermission(
					routeConstants.WALLET,
					menu?.WALLET || menu?.Approval?.wallet_approval_request
				)
				|| checkRoutePermission(routeConstants.WALLET_HISTORY, menu?.History)
				|| checkRoutePermission(routeConstants.SETTINGS, menu?.Settings)
				|| checkRoutePermission(routeConstants.TEMPLATE, menu?.Template)
				|| checkRoutePermission(
					routeConstants.PERSONAL_INFO,
					checkProfilePermissions(menu)
				) || checkRoutePermission(
					routeConstants.PROFILE_EDIT,
					checkProfilePermissions(menu)
				)
				|| checkRoutePermission(
					routeConstants.STATISTICS,
					checkStatisticsPermissions(menu)
				)
				|| checkRoutePermission(
					routeConstants.BULK_ESIGN,
					getPermissions(menu, 'Dashboard', 'bulk_esign', 'bulk_esign_document')
				)
				|| checkRoutePermission(
					routeConstants.NEW_ESIGN,
					getPermissions(menu, 'Dashboard', 'esign', 'new_esign')
				)
				|| checkRoutePermission(routeConstants.EDIT, checkTemplateAddEditPermission(menu))
			) {
				dispatch(updateRedirectRoute(location.pathname));
				return;
			}

			if (Object.keys(menu).length === 0) {
				navigate(routeConstants.PROFILE, { replace: true });
				dispatch(updateRedirectRoute(routeConstants.PROFILE));
			} else {
				menuNavigate(menu, userType);
			}
		} else {
			setIsLoading(false);
			showToast('error', response?.message || response?.error);
		}
	};

	const hasDashboardPermission = (menu) => {
		const dashboardApprovalExists = Boolean(
			menu?.Approval?.dashboard_approval_request
		);
		const dashboardPermissions = Object.values(menu?.Dashboard || {}).flat();
		return (
			dashboardPermissions.some(
				(perm) => perm.permissionName !== 'bulk_new_esign'
			) || dashboardApprovalExists
		);
	};

	const checkTemplateAddEditPermission = (menu) => {
		const addTemplate = getPermissions(menu, 'Template', 'template_engine', 'template_engine_add');
		const editTemplate = getPermissions(menu, 'Template', 'template_engine', 'template_engine_edit');
		return editTemplate || addTemplate;
	};

	const checkProfilePermissions = (menu) => {
		const editProfile = getPermissions(
			menu,
			'Profile',
			'profile',
			'profile_edit'
		);
		const editUserProfile = getPermissions(
			menu,
			'Profile',
			'user',
			'user_profile_edit'
		);
		return editProfile || editUserProfile;
	};

	const checkStatisticsPermissions = (menu) => {
		const viewDigitalStampStats = getPermissions(
			menu,
			'Dashboard',
			'digital_stamp',
			'digital_stamp_statistics'
		);
		const viewEsignStats = getPermissions(
			menu,
			'Dashboard',
			'esign',
			'e-sign_statistics'
		);
		return viewDigitalStampStats || viewEsignStats;
	};

	const menuNavigate = (menu, userType, redirect = true) => {
		const menuItem = Object.keys(menu);
		const sidebarRoutes = sideBarMenu.filter((val) => val.title !== 'Logo');

		const viewDigitalStampStats = getPermissions(
			menu,
			'Dashboard',
			'digital_stamp',
			'digital_stamp_statistics'
		);

		const viewEsignStats = getPermissions(
			menu,
			'Dashboard',
			'esign',
			'e-sign_statistics'
		);

		let finalPath = routeConstants.PROFILE;

		if (
			['organization', 'customer'].includes(userType)
			&& (viewDigitalStampStats || viewEsignStats)
		) {
			finalPath = routeConstants.STATISTICS;
		} else {
			const temp = routerData(sidebarRoutes, menu, menuItem);
			if (temp.length > 0) {
				finalPath = temp[0].link;
			}
		}

		if (redirect) {
			if (window.location.pathname !== finalPath) {
				dispatch(updateRedirectRoute(finalPath));
				window.location.replace(finalPath);
			}
		} else {
			dispatch(updateRedirectRoute(finalPath));
		}
	};

	useEffect(() => {
		const authToken = window.localStorage.getItem(AUTHKEY);
		if (authToken) {
			setUserLoggedIn(true);

			if (window.location.pathname === '/' && userData.menu) {
				const menuItem = Object.keys(userData.menu);
				const sidebarRoutes = sideBarMenu.filter((val) => val.title !== 'Logo');
				const availableRoutes = routerData(sidebarRoutes, userData.menu, menuItem);
				if (availableRoutes.length > 0) {
					navigate(availableRoutes[0].link, { replace: true });
					return;
				}
			}

			if (!userData.menu || userData.brandingChange || !userData?.emailId) {
				fetchMenuDetails();
			} else {
				setMenuItems([userData]);
			}
		} else if (refreshToken) {
			refreshAccessToken();
		} else {
			navigate('/', { replace: true });
			setUserLoggedIn(false);
		}

		if (location.pathname !== routeConstants.EDIT && location.pathname !== '/templatepreview') {
			dispatch(updateCkbuttonStatus(false));
		}

		if (location.pathname !== routeConstants.DASHBOARD) {
			dispatch(checkNotificationStatus(false));
		}

		if (location.pathname !== routeConstants.EDIT) {
			dispatch(setAutoSave(false));
		}
	}, [location, userData, window.localStorage.getItem(AUTHKEY)]);

	useEffect(() => {
		const checkUserChange = () => {
			const token = localStorage.getItem(AUTHKEY);
			const payload = decodeJwtPayload(token);

			const storedEmail = payload?.email;
			const currentEmail = userData?.emailId;
			if (storedEmail && currentEmail && storedEmail !== currentEmail) {
				setIsOpen(true);
			}
		};

		const handleStorageChange = (event) => {
			if (event.key === AUTHKEY) {
				checkUserChange();
			}
		};

		window.addEventListener('storage', handleStorageChange);
		const interval = setInterval(checkUserChange, 3000);

		return () => {
			window.removeEventListener('storage', handleStorageChange);
			clearInterval(interval);
		};
	}, [userData?.emailId]);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [location.pathname]);

	const handleSessionChange = () => {
		window.location.reload();
	};

	return (
		<ThemeProvider theme={theme}>
			<SuccessRejectPopUp
				open={isOpen}
				showImage={false}
				title={'Session Changed!'}
				message={'Your session has been updated. Please reload the page'}
				handleClose={handleSessionChange}
				actionLabel='Reload'
				success
				modalContentWidth='280px'
			/>
			{userLoggedIn
				&& (isMobile ? (
					<Grid container sx={{ height: '100vh' }}>
						<Grid size={12}>
							{menuItems?.length > 0 && <Header menuItems={menuItems?.[0]} />}
						</Grid>
						<Grid
							sx={{
								marginTop: '80px',
								overflow: 'auto',
								paddingBottom: '80px',
								minHeight: 'calc(100vh - 80px)'
							}}
							size={12}
						>
							<Suspense fallback={<></>}>
								{menuItems?.length > 0 ? (
									<Outlet />
								) : (
									<Loading open={isLoading} />
								)}
							</Suspense>
						</Grid>

						<Grid size={12}>{menuItems?.length > 0 && <SideBar />}</Grid>
					</Grid>
				) : (
					<Grid sx={{ width: '100%', mb: '50px' }} container>
						<Grid
							display={'flex'}
							justifyContent={'center'}
							sx={{
								borderRight: 1,
								borderColor: SALT_PAN,
								height: '100vh',
								zIndex: 100,
								bgcolor: 'white'
							}}
							size={{
								xs: 1,
								md: 0.8,
								lg: 0.6
							}}
						>
							{menuItems?.length > 0 && <SideBar />}
						</Grid>
						<Grid
							sx={{ marginTop: '62px' }}
							size={{
								xs: 11,
								md: 11.2,
								lg: 11.4
							}}
						>
							{menuItems?.length > 0 && <Header menuItems={menuItems?.[0]} />}
							<Suspense fallback={<></>}>
								{menuItems?.length > 0 ? (
									<Outlet />
								) : (
									<Loading open={isLoading} />
								)}
							</Suspense>
						</Grid>
					</Grid>
				))}
			{isLoading && (
				<Loading open={isLoading} onClose={() => setIsLoading(false)} />
			)}
		</ThemeProvider>
	);
}
